import React from "react";
import PolicyLayout, { PolicySection } from "../components/PolicyLayout";

const SECTIONS = [
  { id: "collection",   label: "Info We Collect"    },
  { id: "usage",        label: "How We Use It"       },
  { id: "security",     label: "Data & Security"     },
  { id: "cookies",      label: "Cookies"             },
  { id: "account",      label: "Your Account"        },
  { id: "children",     label: "Children's Privacy"  },
  { id: "third-party",  label: "Third Parties"       },
  { id: "changes",      label: "Policy Changes"      },
  { id: "contact",      label: "Contact"             },
];

export default function PrivacyPolicy() {
  return (
    <PolicyLayout
      title="Privacy Policy"
      description="Privacy policy for Khoja Distributions — how we collect, use, and protect your business information."
      canonical="https://www.khojadistributions.com/privacy-policy"
      sections={SECTIONS}
    >
      <PolicySection id="collection" title="1. Information We Collect">
        <p>When you create a wholesale account or use our platform, we collect the following types of information:</p>
        <ul className="space-y-1.5 pl-4 list-disc marker:text-accent">
          <li><strong>Account information:</strong> Name, email address, username, and business name</li>
          <li><strong>Business information:</strong> Shipping address, phone number, and business type</li>
          <li><strong>Order information:</strong> Products ordered, quantities, order history, and delivery details</li>
          <li><strong>Usage data:</strong> Pages visited, search queries, and interactions with the platform</li>
        </ul>
      </PolicySection>

      <PolicySection id="usage" title="2. How We Use Your Information">
        <p>We use the information we collect to:</p>
        <ul className="space-y-1.5 pl-4 list-disc marker:text-accent">
          <li>Process and fulfill your wholesale orders</li>
          <li>Manage your account and provide customer support</li>
          <li>Communicate order updates, delivery status, and account notifications</li>
          <li>Improve our platform and product offerings</li>
          <li>Comply with legal obligations</li>
        </ul>
        <p>We do not sell, rent, or share your personal information with third parties for marketing purposes.</p>
      </PolicySection>

      <PolicySection id="security" title="3. Data Storage & Security">
        <p>Your account and order data is stored securely using Google Firebase, which provides industry-standard encryption and security practices. Passwords are never stored in plain text.</p>
        <p>While we take reasonable measures to protect your information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your data.</p>
      </PolicySection>

      <PolicySection id="cookies" title="4. Cookies & Tracking">
        <p>Our platform uses browser storage (localStorage and sessionStorage) to maintain your session, cart contents, and age verification status. We do not use third-party advertising cookies.</p>
        <p>We may use analytics tools to understand how our platform is used. This data is aggregated and does not identify individual users.</p>
      </PolicySection>

      <PolicySection id="account" title="5. Account Information">
        <p>You can update your account information, shipping address, and contact details at any time by logging into your account and visiting Account Settings.</p>
        <p>To request deletion of your account and associated data, contact us directly at <a href="mailto:khojadistributions@gmail.com" className="text-accent hover:underline">khojadistributions@gmail.com</a>. Note that we may retain order records as required by law or for legitimate business purposes.</p>
      </PolicySection>

      <PolicySection id="children" title="6. Children's Privacy">
        <p>Our platform is intended for business use by adults 21 years of age or older. We do not knowingly collect information from minors. If you believe a minor has created an account, please contact us immediately.</p>
      </PolicySection>

      <PolicySection id="third-party" title="7. Third-Party Services">
        <p>Our platform uses the following third-party services to operate:</p>
        <ul className="space-y-1.5 pl-4 list-disc marker:text-accent">
          <li><strong>Google Firebase</strong> — Authentication, database, and file storage</li>
          <li><strong>Google Fonts</strong> — Typography</li>
        </ul>
        <p>These services have their own privacy policies and data handling practices. We encourage you to review them.</p>
      </PolicySection>

      <PolicySection id="changes" title="8. Changes to This Policy">
        <p>We may update this Privacy Policy from time to time. We will notify registered users of material changes by email or through the platform. Continued use of the platform after changes are posted constitutes acceptance of the revised policy.</p>
      </PolicySection>

      <PolicySection id="contact" title="9. Contact Us">
        <p>If you have questions about this Privacy Policy or how your data is handled, please contact us:</p>
        <p>
          <strong>Khoja Distributions</strong><br />
          Dallas, TX<br />
          Phone: <a href="tel:2146356340" className="text-accent hover:underline">(214) 635-6340</a><br />
          Email: <a href="mailto:khojadistributions@gmail.com" className="text-accent hover:underline">khojadistributions@gmail.com</a>
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
